import { ref, readonly } from 'vue';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

export interface ToastState {
  toasts: ReturnType<typeof readonly<ReturnType<typeof ref<Toast[]>>>>;
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  pauseTimer: (id: string) => void;
  resumeTimer: (id: string) => void;
}

const toasts = ref<Toast[]>([]);
const timers = new Map<string, NodeJS.Timeout>();
const pausedToasts = new Set<string>();

let toastIdCounter = 0;

const generateId = (): string => {
  return `toast-${Date.now()}-${toastIdCounter++}`;
};

export const addToast = (toast: Omit<Toast, 'id'>): string => {
  const id = generateId();
  const duration = toast.duration ?? 5000;

  toasts.value.push({
    id,
    ...toast,
    duration,
  });

  // Auto-remove after duration (unless it's 0 for persistent toasts)
  if (duration > 0) {
    const timer = setTimeout(() => {
      removeToast(id);
    }, duration);
    timers.set(id, timer);
  }

  return id;
};

export const removeToast = (id: string): void => {
  const index = toasts.value.findIndex(t => t.id === id);
  if (index !== -1) {
    toasts.value.splice(index, 1);
  }

  // Clear timer
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
  pausedToasts.delete(id);
};

export const pauseTimer = (id: string): void => {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    pausedToasts.add(id);
  }
};

export const resumeTimer = (id: string): void => {
  if (pausedToasts.has(id)) {
    const toast = toasts.value.find(t => t.id === id);
    if (toast && toast.duration) {
      const timer = setTimeout(() => {
        removeToast(id);
      }, toast.duration);
      timers.set(id, timer);
      pausedToasts.delete(id);
    }
  }
};

// Convenience methods
export const showSuccess = (message: string, title?: string, duration?: number): string => {
  return addToast({ type: 'success', message, title, duration });
};

export const showError = (message: string, title?: string, duration?: number): string => {
  return addToast({ type: 'error', message, title, duration });
};

export const showWarning = (message: string, title?: string, duration?: number): string => {
  return addToast({ type: 'warning', message, title, duration });
};

export const showInfo = (message: string, title?: string, duration?: number): string => {
  return addToast({ type: 'info', message, title, duration });
};

// Composable
export const useToast = (): ToastState => {
  return {
    toasts: readonly(toasts),
    addToast,
    removeToast,
    pauseTimer,
    resumeTimer,
  };
};

// Export helpers for convenience
export const toast = {
  success: showSuccess,
  error: showError,
  warning: showWarning,
  info: showInfo,
  add: addToast,
  remove: removeToast,
};

